import { ArrowRight } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ball from "../../assets/products/ball.png";

const products = [
  {
    id: "01",
    title: "Ball Stud",
    description:
      "We are specialized in manufacturing high-precision ball studs widely used in gas springs for Maruti Suzuki vehicles and other applications.",
    image: ball,
  },
  {
    id: "02",
    title: " Brass Inserts",
    description:
      "These inserts are specialized fasteners designed to be embedded into components during or after the moulding process to create strong, durable, and reusable threaded connections.",
    image:
      "https://cpimg.tistatic.com/797477/b/4/brass-injection-moulding-.jpg",
  },
  {
    id: "03",
    title: "Electronics Connectors & Sockets",
    description:
      "These are essential components widely used in various electrical and electronic systems for establishing secure and conductive connections. ",
    image:
      "https://img500.exportersindia.com/product_images/bc-500/dir_18/538054/brass-electronics-connectors-and-sockets-1529650477-4005790.jpeg",
  },
  {
    id: "04",
    title: "Electric Pins",
    description:
      "Brass Electric Male Female Pins Offers these Electromechanical Parts in various sizes which includes high precision and complex machining of various raw material grades.",
    image:
      "http://5.imimg.com/data5/SELLER/Default/2023/1/NQ/DX/EW/13079167/brass-electric-male-female-pin-500x500.jpg",
  },
  {
    id: "05",
    title: "Core Vents",
    description:
      "Brass and aluminum core vents are small,permeable metal fittings used in foundry industry for sand core and plastic injection molding.",
    image:
      "https://5.imimg.com/data5/ANDROID/Default/2025/4/505885348/KY/IB/ZV/5336163/product-jpeg-1000x1000.jpg",
  },
  {
    id: "06",
    title: "Electric Connectors",
    description:
      "Brass terminal blocks are electrical connectors made primarily from brass, a copper-zinc alloy known for its excellent conductivity, durability, and corrosion resistance.",
    image:
      "https://2.imimg.com/data2/KJ/PA/IMFCP-1518073/brass_neutral_links-1000x1000.jpg",
  },
];

// Robust typewriter hook using cancellable setTimeout chain
function useTypewriter(text, speed = 100) {
  const [displayed, setDisplayed] = useState("");
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Clear any previous timer (important for strict mode / remounts)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Reset displayed each time text or speed changes
    setDisplayed("");

    if (!text) return;

    let i = 0;

    const tick = () => {
      // append next character
      setDisplayed((prev) => prev + text.charAt(i));
      i++;
      if (i < text.length) {
        timeoutRef.current = setTimeout(tick, speed);
      } else {
        timeoutRef.current = null;
      }
    };

    // Start typing (first character after `speed` ms)
    timeoutRef.current = setTimeout(tick, speed);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [text, speed]);

  return displayed;
}

export default function ProductsSection() {
  const animatedHeading = useTypewriter("What we produce", 100);
  const navigate = useNavigate();

  return (
    <div className="font-sans text-gray-800 bg-blue-100">
      <section className="bg-[#000615] text-white px-6 sm:px-10 md:px-20 lg:px-40 py-16 md:py-24">
        {/* Heading */}
        <div className="mb-12 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              <span className="text-lg font-medium">Our Products</span>
            </span>
            <h2 className="text-xl sm:text-3xl md:text-4xl font-semibold mt-2">
              {animatedHeading}
            </h2>
          </div>
          <p className="text-gray-400 text-sm sm:text-base md:text-[16px] font-medium max-w-xl">
            We manufacture high-quality fasteners and components adherence to wide
            range of industries. Our products are recognized for the durability,
            precision, and compliance with global standards.
          </p>
          <button
            className="relative overflow-hidden inline-flex items-center gap-2 px-6 py-3 sm:py-4 rounded-full border border-white  text-white hover:text-black bg-[#000615] hover:bg-white font-medium group transition-colors duration-300"
            data-aos-delay="300"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              navigate("/products");
            }}
          >
            <span className="relative z-10 flex items-center gap-2">
              <span className="relative block overflow-hidden h-6">
                <span className="block transition-transform duration-300 transform group-hover:-translate-y-full">
                  View More
                </span>
                <span className="absolute top-full left-0 w-full block text-black transition-transform duration-300 transform group-hover:-translate-y-full">
                  View More
                </span>
              </span>
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </span>
          </button>
        </div>

        {/* Products List */}
        <div className="divide-y divide-gray-800">
          {products.map((product) => (
            <div
              key={product.id}
              className="group relative flex flex-col md:flex-row items-start md:items-center py-8 sm:py-12 overflow-hidden transition-all duration-500"
            >
              {/* Image for mobile */}
              <div className="block md:hidden mb-4 w-full">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-auto object-cover rounded-lg"
                />
              </div>

              {/* Image for desktop hover */}
              <div className="hidden md:block absolute left-0 w-0 opacity-0 group-hover:w-44 group-hover:opacity-100 transition-all duration-500 ease-in-out">
                <img
                  src={product.image}
                  alt={product.title}
                  className="h-[100px] w-[160px] object-cover rounded-lg"
                />
              </div>

              {/* Text */}
              <div className="flex-1 pl-0 md:group-hover:pl-48 transition-all duration-500 ease-in-out flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <span className="text-lg sm:text-xl md:text-2xl font-semibold text-white">
                    {product.id}
                  </span>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold leading-tight">
                    {product.title}
                  </h3>
                </div>
                <p className="text-gray-400 text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl">
                  {product.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
